<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, CalendarRange, GripVertical, Settings } from 'lucide-vue-next'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

// Configuración global del Gantt
const config = ref({
  title: 'Cronograma del Proyecto',
  dateFormat: 'YYYY-MM-DD',
})

// Estructura de datos del Gantt
const sections = ref<any[]>([])

const initData = () => {
  config.value = {
    title: 'Cronograma del Proyecto',
    dateFormat: 'YYYY-MM-DD',
  }
  sections.value = [
    {
      id: `sec_${Date.now()}`,
      name: 'Fase 1: Planificación',
      tasks: [
        { id: 't1', name: 'Reunión inicial', status: 'done', start: '2024-01-01', duration: '2d' },
        { id: 't2', name: 'Análisis de requisitos', status: 'active', start: 'after t1', duration: '5d' }
      ]
    }
  ]
}

watch(() => props.open, (val) => {
  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && sections.value.length === 0) initData()
}, { immediate: true })

const addSection = () => {
  sections.value.push({
    id: `sec_${Date.now()}`,
    name: 'Nueva Sección',
    tasks: []
  })
}

const addTask = (sectionIndex: number) => {
  const taskNumber = sections.value[sectionIndex].tasks.length + 1
  sections.value[sectionIndex].tasks.push({
    id: `task_${Date.now()}`,
    name: `Tarea ${taskNumber}`,
    status: '', // active, done, crit, milestone
    start: '', // fecha o 'after id'
    duration: '1d'
  })
}

const removeSection = (index: number) => {
  sections.value.splice(index, 1)
}

const removeTask = (sectionIndex: number, taskIndex: number) => {
  sections.value[sectionIndex].tasks.splice(taskIndex, 1)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\ngantt\n'
  mermaid += `    title ${config.value.title}\n`
  mermaid += `    dateFormat ${config.value.dateFormat}\n\n`
  
  sections.value.forEach(sec => {
    mermaid += `    section ${sec.name}\n`
    sec.tasks.forEach((t: any) => {
      let line = `    ${t.name.padEnd(20, ' ')} :`
      
      const properties = []
      if (t.status) properties.push(t.status)
      if (t.id) properties.push(t.id)
      if (t.start) properties.push(t.start)
      if (t.duration) properties.push(t.duration)
      
      line += properties.join(', ')
      mermaid += line + '\n'
    })
    mermaid += '\n'
  })

  mermaid += '```\n'
  return mermaid
}

watch([config, sections], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const newSections: any[] = []
    let currentSection: any = null
    
    lines.forEach(line => {
      line = line.trim()
      if (!line || line.startsWith('```') || line.startsWith('gantt')) return

      if (line.startsWith('title ')) {
        config.value.title = line.replace('title ', '').trim()
        return
      }
      
      if (line.startsWith('dateFormat ')) {
        config.value.dateFormat = line.replace('dateFormat ', '').trim()
        return
      }

      if (line.startsWith('section ')) {
        currentSection = {
          id: `sec_${Date.now()}_${Math.random()}`,
          name: line.replace('section ', '').trim(),
          tasks: []
        }
        newSections.push(currentSection)
        return
      }

      // Tareas: Nombre de la tarea : [estado], [id], [inicio], [duración]
      if (line.includes(':') && currentSection) {
        const parts = line.split(':')
        const name = parts[0].trim()
        const propsRaw = parts.slice(1).join(':').split(',').map(s => s.trim())
        
        let status = ''
        let id = ''
        let start = ''
        let duration = ''

        // Heurística simple para inferir las propiedades posicionales o etiquetadas de Mermaid Gantt
        propsRaw.forEach(prop => {
          const p = prop.toLowerCase()
          if (['done', 'active', 'crit', 'milestone'].includes(p)) {
            status = p
          } else if (p.match(/^\d+[dwM]$/) || p.match(/^\d{4}-\d{2}-\d{2}$/) || p.startsWith('after ')) {
            // Si parece una fecha, una duración o un 'after id'
            if (p.startsWith('after ') || p.match(/^\d{4}-\d{2}-\d{2}$/)) {
              start = prop // Preservar camelCase/mayúsculas
            } else {
              duration = prop
            }
          } else {
            // Asumimos que es el ID (normalmente es el primer elemento no reservado si no es estado)
            if (!id) id = prop
          }
        })

        currentSection.tasks.push({
          id: id || `task_${Date.now()}_${Math.random()}`,
          name,
          status,
          start,
          duration
        })
      }
    })

    if (newSections.length > 0) {
      sections.value = newSections
    }
  } catch (error) {
    console.error("Error parsing gantt code:", error)
  } finally {
    setTimeout(() => { isParsing.value = false }, 100)
  }
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (e) {
    console.error('Failed to copy', e)
  }
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Gantt"
    description="Construye un cronograma organizando tus tareas en secciones. Define la duración (ej. 2d) o dependencias (ej. after t1)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <CalendarRange class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addSection"><Plus class="w-4 h-4 mr-2" /> Añadir Sección</Button>
    </template>

    <template #canvas>
      <div class="absolute inset-0 overflow-y-auto bg-slate-50 dark:bg-slate-900 p-4">
        <div class="max-w-4xl mx-auto flex flex-col gap-6 pb-20">
          
          <!-- Configuración Global -->
          <div class="bg-card border rounded-lg p-4 shadow-sm">
            <h3 class="text-sm font-semibold mb-3 flex items-center text-muted-foreground"><Settings class="w-4 h-4 mr-2" /> Configuración Global</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1 text-sm">
                <label>Título del Gráfico</label>
                <input v-model="config.title" class="border rounded p-2 bg-background focus:ring-1 focus:ring-primary outline-none" />
              </div>
              <div class="flex flex-col gap-1 text-sm">
                <label>Formato de Fecha (dateFormat)</label>
                <input v-model="config.dateFormat" class="border rounded p-2 bg-background focus:ring-1 focus:ring-primary outline-none font-mono" placeholder="YYYY-MM-DD" />
              </div>
            </div>
          </div>

          <!-- Secciones y Tareas -->
          <div v-for="(sec, sIdx) in sections" :key="sec.id" class="bg-card border-2 border-primary/20 rounded-lg p-0 shadow-sm overflow-hidden">
            <!-- Header de Sección -->
            <div class="bg-primary/5 border-b border-primary/20 p-3 flex items-center justify-between">
              <div class="flex items-center gap-2 flex-1">
                <GripVertical class="w-4 h-4 text-muted-foreground cursor-grab opacity-50" />
                <input v-model="sec.name" class="bg-transparent font-bold text-lg outline-none w-full border-b border-transparent focus:border-primary px-1" placeholder="Nombre de Sección" />
              </div>
              <div class="flex items-center gap-2">
                <Button variant="ghost" size="sm" @click="addTask(sIdx)" class="h-8"><Plus class="w-4 h-4 mr-1" /> Tarea</Button>
                <button @click="removeSection(sIdx)" class="text-destructive hover:bg-destructive/10 p-1.5 rounded"><Trash2 class="w-4 h-4" /></button>
              </div>
            </div>

            <!-- Lista de Tareas -->
            <div class="p-3 flex flex-col gap-2">
              <div v-if="sec.tasks.length === 0" class="text-xs text-muted-foreground italic text-center p-4">No hay tareas en esta sección.</div>
              
              <div v-for="(task, tIdx) in sec.tasks" :key="task.id" class="flex flex-wrap md:flex-nowrap items-start gap-2 bg-background border rounded p-2 group hover:border-primary/50 transition-colors">
                
                <div class="flex flex-col gap-1 flex-1 min-w-[200px]">
                  <label class="text-[10px] text-muted-foreground uppercase font-semibold">Nombre de Tarea</label>
                  <input v-model="task.name" class="text-sm border-b border-transparent focus:border-primary bg-transparent outline-none w-full" placeholder="Nombre" />
                </div>
                
                <div class="flex flex-col gap-1 w-[80px]">
                  <label class="text-[10px] text-muted-foreground uppercase font-semibold">ID</label>
                  <input v-model="task.id" class="text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none font-mono" placeholder="id" />
                </div>

                <div class="flex flex-col gap-1 w-[100px]">
                  <label class="text-[10px] text-muted-foreground uppercase font-semibold">Estado</label>
                  <select v-model="task.status" class="text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none">
                    <option value="">Normal</option>
                    <option value="done">Completado</option>
                    <option value="active">Activo</option>
                    <option value="crit">Crítico</option>
                    <option value="milestone">Hito</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1 w-[120px]">
                  <label class="text-[10px] text-muted-foreground uppercase font-semibold">Inicio</label>
                  <input v-model="task.start" class="text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none" placeholder="2024-01-01 o 'after t1'" />
                </div>

                <div class="flex flex-col gap-1 w-[80px]">
                  <label class="text-[10px] text-muted-foreground uppercase font-semibold">Duración</label>
                  <input v-model="task.duration" class="text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none" placeholder="2d, 1w" />
                </div>

                <div class="flex flex-col justify-end pb-0.5">
                  <button @click="removeTask(sIdx, tIdx)" class="text-destructive/50 hover:text-destructive p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
